import { useMMDataContext } from 'src/features/MM/contexts/MMDataContext';
import { useCPULabel } from 'src/features/MM/hooks/useCPULabel';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { useKpiUnit } from 'src/hooks/useKpiUnit';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import {
  currencyValueTickFormatter,
  PercentageValueTickFormatter,
  ValueTickFormatter,
} from 'src/utils/TickFormatters';
import { useCurrency } from 'src/hooks/useCurrency';
import { getCurrencyName } from 'src/utils/currency';
import { useRoiDef } from 'src/utils/calculateRoi';
import {
  incrementalMetricDef,
  mediaEffectDef,
  MEDIA_INVESTMENT_DEF,
  romiDef,
  totalRoiDef,
  totalRomiDef,
  getCPUDef,
  getTotalCPUDef,
} from 'src/utils/metricDefinitions';
import { TrendsChartBox } from '../../organisms/TrendsChartBox';
import { TrendTabs, TrendsType } from '../../molecules/TrendTabs';
import { CumulativeSpendChart } from '../../molecules/CumulativeSpendChart';
import { PacingHealthChart } from '../../molecules/PacingHealthChart';
import { DailySpendWithROIChart } from '../../molecules/DailySpendWithROIChart';
import { WeeklySpendHeatmap } from 'src/components/molecules/WeeklySpendHeatmap';
import { useState } from 'react';
import { Box, Grid, GridItem, Text, useTheme } from '@chakra-ui/react';
import { IS_DEV } from 'src/constants';
import { CampaignCalendar } from 'src/components/molecules/CampaignCalendar';
import {
  mockChannels,
  mockEvents,
} from 'src/components/molecules/CampaignCalendar/mockData';

const colSpan = 12;
const minH = 350;

// Mock data for pacing view - this should be replaced with actual data from API
const mockCumulativeSpendData = [
  {
    date: '2024-05-01',
    utcEpoch: 1714521600000,
    actualSpend: 320,
    idealSpend: 270,
  },
  {
    date: '2024-05-02',
    utcEpoch: 1714608000000,
    actualSpend: 310,
    idealSpend: 300,
  },
  {
    date: '2024-05-03',
    utcEpoch: 1714694400000,
    actualSpend: 390,
    idealSpend: 370,
  },
  {
    date: '2024-05-04',
    utcEpoch: 1714780800000,
    actualSpend: 450,
    idealSpend: 400,
  },
  {
    date: '2024-05-05',
    utcEpoch: 1714867200000,
    actualSpend: 350,
    idealSpend: 320,
  },
  {
    date: '2024-05-06',
    utcEpoch: 1714953600000,
    actualSpend: 290,
    idealSpend: 240,
  },
  {
    date: '2024-05-07',
    utcEpoch: 1715040000000,
    actualSpend: 430,
    idealSpend: 380,
  },
];

const mockPacingHealthData = {
  actualSpend: 310,
  idealSpend: 300,
  pacingHealth: 53,
};

// Mock data for Daily Spend with ROI chart
const mockDailySpendWithROIData = [
  {
    date: '2024-05-01',
    utcEpoch: 1714521600000,
    dailySpend: 90,
    roi: 0.6,
  },
  {
    date: '2024-05-02',
    utcEpoch: 1714608000000,
    dailySpend: 100,
    roi: 1.0,
  },
  {
    date: '2024-05-03',
    utcEpoch: 1714694400000,
    dailySpend: 90,
    roi: 0.8,
  },
  {
    date: '2024-05-04',
    utcEpoch: 1714780800000,
    dailySpend: 80,
    roi: 0.7,
  },
  {
    date: '2024-05-05',
    utcEpoch: 1714867200000,
    dailySpend: 70,
    roi: 0.6,
  },
  {
    date: '2024-05-06',
    utcEpoch: 1714953600000,
    dailySpend: 85,
    roi: 0.8,
  },
  {
    date: '2024-05-07',
    utcEpoch: 1715040000000,
    dailySpend: 60,
    roi: 0.7,
  },
];

// Mock data for Weekly Spend Heatmap
const mockWeeklySpendHeatmapData = [
  {
    day: 'Mon',
    category1: 6.2,
    category2: 4.4,
    category3: 3.2,
  },
  {
    day: 'Tue',
    category1: 3.2,
    category2: 4.4,
    category3: 9.2,
  },
  {
    day: 'Wed',
    category1: 9.2,
    category2: 6.2,
    category3: 4.4,
  },
  {
    day: 'Thu',
    category1: 3.2,
    category2: 1.5,
    category3: 4.4,
  },
  {
    day: 'Fri',
    category1: 9.2,
    category2: 4.4,
    category3: 6.2,
  },
  {
    day: 'Sat',
    category1: 4.4,
    category2: 3.2,
    category3: 6.2,
  },
  {
    day: 'Sun',
    category1: 3.2,
    category2: 1.5,
    category3: 6.2,
  },
];

export const Trends: React.FC = () => {
  const cpuLabel = useCPULabel();
  const { metricLabel, incrementalLabel } = useKpiMetricLabels();
  const theme = useTheme();
  const { roi, romi, romiEnabled, mediaEffect, cpu, decomps, investments } =
    useMMDataContext();

  const { unit } = useKpiUnit();
  const { code: currencyCode } = useCurrency();

  // Tab label for monetary values follows the customer currency
  // ('Euro', 'Swedish Krona', ...); non-monetary KPIs show 'Unit'
  const currencyTabLabel = getCurrencyName(currencyCode);
  const unitTabLabel = unit === 'euro' ? currencyTabLabel : 'Unit';

  const incrementalSalesTickFormatter = useChartEffectTickFormatter();
  const investmentTickFormatter = currencyValueTickFormatter(currencyCode);

  const roiDef = useRoiDef();

  const [selectedTab, setSelectedTab] = useState<TrendsType>('trends');

  return (
    <>
      {IS_DEV && (
        <TrendTabs onSelect={setSelectedTab} selectedTab={selectedTab} />
      )}
      {selectedTab === 'trends' ? (
        <>
          {romiEnabled ? (
            <TrendsChartBox
              title="ROMI"
              totalLineLabel="Total ROMI"
              colSpan={colSpan}
              minH={minH}
              metricState={romi}
              yAxisFormatter={ValueTickFormatter}
              infoBodyText={`${romiDef(metricLabel)}\n\n${totalRomiDef(metricLabel)}`}
              infoTitle="Return on Marketing Investment"
            />
          ) : (
            <TrendsChartBox
              title="ROI"
              totalLineLabel="Total ROI"
              colSpan={colSpan}
              minH={minH}
              metricState={roi}
              yAxisFormatter={ValueTickFormatter}
              infoBodyText={`${roiDef}\n\n${totalRoiDef(metricLabel)}`}
              infoTitle="Return on Investment"
            />
          )}
          <TrendsChartBox
            title="Media investment"
            colSpan={colSpan}
            minH={minH}
            metricState={investments}
            unitTabLabel={currencyTabLabel}
            yAxisFormatter={investmentTickFormatter}
            infoBodyText={MEDIA_INVESTMENT_DEF}
          />
          <TrendsChartBox
            title={incrementalLabel}
            colSpan={colSpan}
            minH={minH}
            metricState={decomps}
            unitTabLabel={unitTabLabel}
            yAxisFormatter={incrementalSalesTickFormatter}
            infoBodyText={incrementalMetricDef(metricLabel)}
          />
          <TrendsChartBox
            title={cpuLabel}
            totalLineLabel={`Total ${cpuLabel}`}
            colSpan={colSpan}
            minH={minH}
            metricState={cpu}
            yAxisFormatter={investmentTickFormatter}
            infoBodyText={`${getCPUDef(cpuLabel, metricLabel)}\n\n${getTotalCPUDef(cpuLabel, metricLabel)}`}
          />
          <TrendsChartBox
            title={'Media effect'}
            colSpan={colSpan}
            minH={minH}
            metricState={mediaEffect}
            yAxisFormatter={PercentageValueTickFormatter}
            infoBodyText={mediaEffectDef(metricLabel)}
          />
        </>
      ) : selectedTab === 'pacingView' ? (
        <>
          <GridItem colSpan={12}>
            <Grid templateColumns="repeat(12, 1fr)" gap={6} templateRows="1fr">
              <GridItem colSpan={8}>
                <Box
                  bg="surface.panel"
                  borderRadius="lg"
                  p={6}
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="surface.border"
                  height="100%"
                  display="flex"
                  flexDirection="column">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={4}>
                    <Text fontSize="lg" fontWeight="semibold">
                      Cumulative Spend: Actual vs Ideal
                    </Text>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          w="3"
                          h="3"
                          bg={theme.colors.chart.line.blue}
                          borderRadius="sm"
                        />
                        <Text fontSize="sm" color="content.muted">
                          Actual
                        </Text>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box w="3" h="3" bg="#22c55e" borderRadius="sm" />
                        <Text fontSize="sm" color="content.muted">
                          Ideal
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                  <Box flex="1">
                    <CumulativeSpendChart data={mockCumulativeSpendData} />
                  </Box>
                </Box>
              </GridItem>
              <GridItem colSpan={4}>
                <Box
                  bg="surface.panel"
                  borderRadius="lg"
                  p={6}
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="surface.border"
                  height="100%"
                  display="flex"
                  flexDirection="column">
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Pacing Health
                  </Text>
                  <Box flex="1">
                    <PacingHealthChart data={mockPacingHealthData} />
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          </GridItem>

          {/* Daily Spend with ROI Chart */}
          <GridItem colSpan={12}>
            <Box
              bg="surface.panel"
              borderRadius="lg"
              p={6}
              boxShadow="sm"
              border="1px solid"
              borderColor="surface.border"
              height="100%"
              display="flex"
              flexDirection="column">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Daily Spend with ROI
                </Text>
                <Box display="flex" alignItems="center" gap={4}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      w="3"
                      h="3"
                      bg={theme.colors.chart.line.blue}
                      borderRadius="sm"
                    />
                    <Text fontSize="sm" color="content.muted">
                      ROI
                    </Text>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      w="3"
                      h="3"
                      bg={theme.colors.chart.bar.lightGreen}
                      borderRadius="sm"
                    />
                    <Text fontSize="sm" color="content.muted">
                      Daily Spend
                    </Text>
                  </Box>
                </Box>
              </Box>
              <Box flex="1">
                <DailySpendWithROIChart data={mockDailySpendWithROIData} />
              </Box>
            </Box>
          </GridItem>

          {/* Weekly Spend Heatmap */}
          <GridItem colSpan={12}>
            <Box
              bg="surface.panel"
              borderRadius="lg"
              p={6}
              boxShadow="sm"
              border="1px solid"
              borderColor="surface.border"
              height="100%"
              display="flex"
              flexDirection="column">
              <Box flex="1">
                <WeeklySpendHeatmap data={mockWeeklySpendHeatmapData} />
              </Box>
            </Box>
          </GridItem>
        </>
      ) : (
        <GridItem colSpan={12}>
          <CampaignCalendar
            startDate="2026-08-03"
            endDate="2026-08-10"
            channels={mockChannels}
            events={mockEvents}
          />
        </GridItem>
      )}
    </>
  );
};
